import Swiper from 'swiper/dist/js/swiper';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import Bowser from 'bowser/bundled';
import domReady from '../helpers/dom-ready';

class WCSwiper {
  static init(...parameters) {
    return new WCSwiper(...parameters);
  }

  constructor() {
    this.container = document.querySelector('.product-images-wrapper');

    if (this.container) {
      this.imageThumbnails = this.container.querySelectorAll('.thumbnail');
      this.thumbnailSwiperContainer = this.container.querySelector('.product-images .swiper-container');
      this.thumbnailSwiperPagination = this.container.querySelector('.product-images .thumbnails-pagination');
      this.zoomedImageWrapper = this.container.querySelector('.zoomed-image');
      this.zoomSwiperContainer = this.zoomedImageWrapper.querySelector('.swiper-container');
      this.zoomedImageCloseButton = this.zoomedImageWrapper.querySelector('.close-button');
      this.zoomSwiperSlideButtons = this.zoomedImageWrapper.querySelectorAll('.swiper-button');
      this.imageOverlay = this.container.querySelector('.image-overlay');
      this.mainImages = this.container.querySelectorAll('.main-image');
      this.thumbnailsContainer = this.container.querySelector('.product-images .thumbnails');

      if (this.thumbnailSwiperPagination) {
        this.thumbnailSwiperNextSlideButton = this.thumbnailSwiperPagination.querySelector('.swiper-button.-next');
        this.thumbnailSwiperPrevSlideButton = this.thumbnailSwiperPagination.querySelector('.swiper-button.-prev');

        this.setUpThumbnailSwiper();

        this.maybeHidePaginationButtons();

        window.addEventListener('resize', () => {
          this.maybeHidePaginationButtons();
        });
      }

      const userAgent = Bowser.getParser(window.navigator.userAgent);
      const browser = userAgent.parsedResult.browser.name;
      const os = userAgent.parsedResult.os.name;
      this.isIosSafari = os === 'iOS' && browser === 'Safari';

      this.thumbnailsList = this.container.querySelector('.thumbnails');

      this.setUpZoomSwiper();

      this.thumbnailSwiperContainer.addEventListener('click', () => {
        this.openZoomImage();
      });

      this.zoomedImageCloseButton.addEventListener('click', () => {
        this.closeZoomImage();
      });

      [...this.imageThumbnails].forEach((thumbnail, index) => {
        // Add active class to first thumbnail on mount
        if (index === 0) {
          thumbnail.classList.add('-active');
        }

        thumbnail.addEventListener('click', (event) => {
          this.selectImage(event, index);
        });
      });
    }
  }

  maybeHidePaginationButtons() {
    if (this.thumbnailSwiperContainer.scrollWidth - 104 >= [...this.imageThumbnails].length * 120) {
      this.thumbnailSwiperNextSlideButton.classList.add('-hidden');
      this.thumbnailSwiperPrevSlideButton.classList.add('-hidden');
      this.thumbnailsContainer.classList.add('-center-content');
    } else {
      this.thumbnailSwiperNextSlideButton.classList.remove('-hidden');
      this.thumbnailSwiperPrevSlideButton.classList.remove('-hidden');
      this.thumbnailsContainer.classList.remove('-center-content');
    }
  }

  setUpThumbnailSwiper() {
    // NOTE: It's not possible to change effect for an initilized instance of Swiper
    // thus we need to destroy the old instance and initilize a new one with new settings
    const swiperPagination = this.container.querySelector('.product-images .swiper-pagination');
    const isMobile = window.innerWidth < 768;

    this.thumbnailSwiperNextSlideButton.addEventListener('click', () => {
      this.thumbnailsContainer.scrollBy({
        left: 120,
        behavior: 'smooth',
      });
    });
    this.thumbnailSwiperPrevSlideButton.addEventListener('click', () => {
      this.thumbnailsContainer.scrollBy({
        left: -120,
        behavior: 'smooth',
      });
    });

    const mobileSettings = {
      loop: true,
      pagination: {
        el: swiperPagination,
        type: 'bullets',
      },
    };

    const desktopSettings = {
      allowTouchMove: false,
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
    };

    if (!this.thumbnailSwiper) {
      this.thumbnailSwiper = new Swiper(
        this.thumbnailSwiperContainer,
        isMobile ? mobileSettings : desktopSettings,
      );
    } else {
      const { effect } = this.thumbnailSwiper.originalParams;

      if (isMobile && effect !== 'slide') {
        this.thumbnailSwiper.destroy(true, true);
        this.thumbnailSwiper = new Swiper(this.thumbnailSwiperContainer, mobileSettings);
      }

      if (!isMobile && effect !== 'fade') {
        this.thumbnailSwiper.destroy(true, true);
        this.thumbnailSwiper = new Swiper(this.thumbnailSwiperContainer, desktopSettings);
      }
    }
  }

  setUpZoomSwiper() {
    const zoomSwiperNextSlideButton = this.zoomedImageWrapper.querySelector('.swiper-button.-next');
    const zoomSwiperPrevSlideButton = this.zoomedImageWrapper.querySelector('.swiper-button.-prev');

    this.zoomSwiper = new Swiper(this.zoomSwiperContainer, {
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
      initialSlide: 0,
      loop: true,
      navigation: {
        nextEl: zoomSwiperNextSlideButton,
        prevEl: zoomSwiperPrevSlideButton,
      },
      observer: true,
      observeParents: true,
    });
  }

  openZoomImage() {
    this.zoomedImageWrapper.classList.add('-show');
    this.zoomedImageWrapper.classList.add('-animate-in');
    this.zoomSwiperContainer.classList.add('-animate-in');

    setTimeout(() => {
      [...this.zoomSwiperSlideButtons].forEach((button) => { button.classList.add('-animate-in'); });
    }, 200);

    this.zoomedImageWrapper.classList.remove('-animate-out');
    this.zoomSwiperContainer.classList.remove('-animate-out');

    const swiperHeight = getComputedStyle(document.documentElement).getPropertyValue('--inner-height');
    this.swiperSlides = this.zoomedImageWrapper.querySelectorAll('.swiper-slide');

    if (this.isIosSafari) {
      [...this.swiperSlides].forEach((slide) => {
        const slideImage = slide.firstElementChild;
        const slideImageStyle = slideImage.style;
        slideImageStyle.maxHeight = swiperHeight;
      });
    }

    disableBodyScroll([...this.swiperSlides][0]);

    if (this.zoomSwiper && this.thumbnailSwiper) {
      if (this.thumbnailSwiper.params.loop) {
        this.zoomSwiper.slideToLoop(this.thumbnailSwiper.activeIndex - 1, 0);
      } else {
        this.zoomSwiper.slideToLoop(this.thumbnailSwiper.activeIndex, 0);
      }
    }
  }

  closeZoomImage() {
    this.zoomedImageWrapper.classList.add('-animate-out');
    this.zoomSwiperContainer.classList.add('-animate-out');

    this.zoomedImageWrapper.classList.remove('-animate-in');
    this.zoomSwiperContainer.classList.remove('-animate-in');

    setTimeout(() => {
      this.zoomedImageWrapper.classList.remove('-show');
      [...this.zoomSwiperSlideButtons].forEach((button) => { button.classList.remove('-animate-in'); });
    }, 200);

    enableBodyScroll([...this.swiperSlides][0]);

    if (this.thumbnailSwiper && this.zoomSwiper) {
      if (this.thumbnailSwiper.params.loop) {
        this.thumbnailSwiper.slideToLoop(this.zoomSwiper.activeIndex - 1, 0);
      } else {
        this.thumbnailSwiper.slideTo(this.zoomSwiper.activeIndex - 1, 0);
      }
    }

    [...this.imageThumbnails].forEach((thumbnail, index) => {
      if (index === this.zoomSwiper.activeIndex - 1) {
        thumbnail.classList.add('-active');
      } else {
        thumbnail.classList.remove('-active');
      }
    });
  }

  selectImage(event, index) {
    const clickedThumbnail = event.currentTarget;

    if (this.thumbnailSwiper.params.loop) {
      this.thumbnailSwiper.slideToLoop(index);
    } else {
      this.thumbnailSwiper.slideTo(index);
    }

    [...this.imageThumbnails].forEach((thumbnail) => {
      if (thumbnail !== clickedThumbnail && thumbnail.classList.contains('-active')) {
        thumbnail.classList.remove('-active');
      } else {
        clickedThumbnail.classList.add('-active');
      }
    });
  }
}

domReady(() => {
  WCSwiper.init();
});
